import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar as CalendarIcon, User, Phone, Mail, Clock, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, completed, cancelled

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${BACKEND_URL}/api/admin/bookings/${bookingId}`,
        { status: newStatus },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast.success(`Booking ${newStatus}`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Booking deleted successfully');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-600/20 text-green-500';
      case 'completed': return 'bg-blue-600/20 text-blue-500';
      case 'cancelled': return 'bg-red-600/20 text-red-500';
      default: return 'bg-yellow-600/20 text-yellow-500';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-black text-white uppercase">Bookings Management</h2>
        
        <div className="flex gap-2">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className={filter === 'all' ? 'bg-red-600 text-white' : 'border-zinc-700 text-gray-400'}
          >
            All ({bookings.length})
          </Button>
          <Button
            onClick={() => setFilter('pending')}
            variant={filter === 'pending' ? 'default' : 'outline'}
            className={filter === 'pending' ? 'bg-red-600 text-white' : 'border-zinc-700 text-gray-400'}
          >
            Pending
          </Button>
          <Button
            onClick={() => setFilter('confirmed')}
            variant={filter === 'confirmed' ? 'default' : 'outline'}
            className={filter === 'confirmed' ? 'bg-red-600 text-white' : 'border-zinc-700 text-gray-400'}
          >
            Confirmed
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {filteredBookings.map((booking) => (
          <Card key={booking.id} className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Client Info */}
                <div>
                  <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-red-600" />
                    Client Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300">
                      <span className="font-semibold">Name:</span> {booking.name}
                    </p>
                    <p className="text-gray-300 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-red-600" />
                      {booking.email}
                    </p>
                    <p className="text-gray-300 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-red-600" />
                      {booking.phone}
                    </p>
                  </div>
                </div>

                {/* Appointment Details */}
                <div>
                  <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-red-600" />
                    Appointment Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300">
                      <span className="font-semibold">Artist:</span> {booking.artist}
                    </p>
                    <p className="text-gray-300">
                      <span className="font-semibold">Service:</span> {booking.service}
                    </p>
                    <p className="text-gray-300 flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-red-600" />
                      {booking.date}
                    </p>
                    <p className="text-gray-300 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-red-600" />
                      {booking.time}
                    </p>
                    {booking.description && (
                      <p className="text-gray-400 text-xs mt-2 italic">
                        "{booking.description}"
                      </p>
                    )}
                  </div>
                </div>

                {/* Status & Actions */}
                <div>
                  <h3 className="text-white font-bold text-lg mb-3">Status & Actions</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 text-xs font-bold rounded uppercase ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>

                    <Select
                      value={booking.status}
                      onValueChange={(value) => handleStatusChange(booking.id, value)}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button
                      onClick={() => handleDelete(booking.id)}
                      variant="destructive"
                      size="sm"
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Booking
                    </Button>

                    <p className="text-gray-500 text-xs">
                      Created: {new Date(booking.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredBookings.length === 0 && (
        <div className="text-center py-20">
          <CalendarIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No bookings found</p>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
